import { useState, useEffect, useRef } from 'react'
import { searchApplicants, type ApplicantSearchResult } from '../domain/cxAdminApi'

interface UseSearchApplicantsResult {
  data: ApplicantSearchResult[] | null
  loading: boolean
  error: string | null
}

/**
 * Searches applicants via the legacy insta_admin applicants search API, exposing
 * the `{ data, loading, error }` shape of the codegen query hooks so components
 * and tests can treat it like any other read.
 */
export const useSearchApplicants = (query: string): UseSearchApplicantsResult => {
  const [data, setData] = useState<ApplicantSearchResult[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const trimmed = query.trim()
    if (trimmed.length === 0) {
      setData(null)
      setLoading(false)
      setError(null)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    let active = true

    setLoading(true)
    setError(null)
    searchApplicants(trimmed)
      .then(results => {
        if (active) setData(results)
      })
      .catch((err: unknown) => {
        if (!active) return
        setData(null)
        setError(err instanceof Error ? err.message : 'Failed to search applicants')
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
      controller.abort()
    }
  }, [query])

  return { data, loading, error }
}
